import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMe } from "../hooks/useMe";

const IconHome = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 7l6-5 6 5v7a1 1 0 0 1-1 1h-3v-5H6v5H3a1 1 0 0 1-1-1V7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);
const IconCases = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="4" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M5 4V2.5A.5.5 0 0 1 5.5 2h5a.5.5 0 0 1 .5.5V4" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);
const IconPlus = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const IconUsers = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="6" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M1.5 14c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="11.5" cy="5" r="2" stroke="currentColor" strokeWidth="1.4" />
    <path d="M14.5 12.5c-.4-1.5-1.5-2.5-3-2.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const IconGear = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3 3l1.5 1.5M11.5 11.5L13 13M3 13l1.5-1.5M11.5 4.5L13 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const ADMIN_NAV = [
  { key: "cases", label: "All Cases", icon: IconCases, to: "/admin/cases" },
  { key: "new", label: "New Case", icon: IconPlus, to: "/case-form/new/0" },
  { key: "client", label: "Client Setup", icon: IconUsers, to: "/client/new" },
];

const USER_NAV = [
  { key: "portal", label: "Active Cases", icon: IconCases, to: "/secure-portal" },
  { key: "account", label: "My Account", icon: IconGear, to: "/account" },
];

function isActive(item: { to: string }, location: ReturnType<typeof useLocation>) {
  if (item.to === "/admin/cases") return location.pathname.includes("admin");
  if (item.to === "/case-form/new/0") return location.pathname.includes("case-form");
  if (item.to === "/client/new") return location.pathname.includes("client") && !location.pathname.includes("admin");
  return location.pathname === item.to || location.pathname.startsWith(item.to + "/");
}

export default function Drawer({ noAuth }: { noAuth?: boolean }) {
  const location = useLocation();
  const me = useMe();

  const isAdmin = useMemo(() => me?.isAdmin, [me?.isAdmin]);
  const displayName = [me?.firstName, me?.lastName].filter(Boolean).join(" ").trim();
  const initials = [me?.firstName?.[0], me?.lastName?.[0]].filter(Boolean).join("").toUpperCase();

  const navItems = isAdmin ? ADMIN_NAV : USER_NAV;

  return (
    <aside className="p-sidebar">
      <Link to="/" className="p-brand">
        <div className="p-brand-mark">P|T</div>
        <div className="p-brand-name">
          Preferred Title
          <small>Secure Portal</small>
        </div>
      </Link>

      {!noAuth && (
        <>
          <div className="p-nav-section">
            <div className="p-nav-label">Workspace</div>
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  className={`p-nav-item${isActive(item, location) ? " active" : ""}`}
                >
                  <Icon />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          <div className="p-user">
            <div className="p-avatar">{initials || "PT"}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cream)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {displayName || "User"}
              </div>
              <div style={{ fontSize: 11, color: "rgba(246,237,228,.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {me?.email || ""}
              </div>
            </div>
          </div>
        </>
      )}
    </aside>
  );
}
